import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { translate } from '@jsverse/transloco';
import { catchError, map } from 'rxjs';
import { type CommentModel, ExplodedCommentModel, withLocalCommentVisibility } from './models/CommentModel';
import type { CommentModelCreateRequestDTO } from './models/CommentModelCreateRequestDTO';
import type { UpdateCommentModelResponse, UpdatedConfiguration } from './models/UpdateCommentResponse';

@Injectable({
  providedIn: 'root',
})
export class CommentApiService {
  private readonly snack = inject(MatSnackBar);
  private readonly http = inject(HttpClient);
  private readonly localVisibilityById = new Map<string, boolean>();
  /**
   *
   */
  private readonly baseUrl = '/api/comment/v2/comments';
  /**
   *
   * @param id
   * @returns
   */
  getComment(id: string) {
    return this.http.get<CommentModel>(`${this.baseUrl}/${id}`).pipe(
      map((comment) => this.normalizeCommentVisibility(comment)),
      map((comment) => new ExplodedCommentModel(comment))
    );
  }
  /**
   *
   * @param comment
   * @returns
   */
  createComment(reqBody: CommentModelCreateRequestDTO) {
    return this.http.post<CommentModel>(`${this.baseUrl}`, reqBody).pipe(
      map((comment) => {
        // Seed local visibility from the DTO before normalising, so that a
        // comment created with showInLane=false is immediately hidden without
        // needing a separate toggle action.
        if (!this.localVisibilityById.has(comment.identifier)) {
          this.localVisibilityById.set(comment.identifier, reqBody.showInLane);
        }
        return this.normalizeCommentVisibility(comment);
      })
    );
  }
  /**
   *
   * @param comment
   * @returns
   */
  updateComment(comment: CommentModelCreateRequestDTO) {
    if (!comment.identifier) {
      throw new Error('Comment identifier is required for update');
    }
    this.setLocalVisibility(comment.identifier, comment.showInLane);

    return this.http
      .put<UpdateCommentModelResponse>(`${this.baseUrl}`, {
        ...comment,
        mappedChannels: comment.mappedChannels.filter(Boolean),
      })
      .pipe(
        map((response) => ({
          ...response,
          updatedComment: this.normalizeCommentVisibility(response.updatedComment as unknown as CommentModel) as unknown as UpdatedConfiguration,
        }))
      )
      .pipe(
        catchError((err) => {
          this.snack.open(err.error?.reason ?? translate(`ERROR.${err.status}`), translate('Dismiss'));
          return [];
        })
      );
  }
  /**
   *
   * @param channelId
   * @returns
   */
  getCommentsOfOneChannel({ channelId, endTime, startTime }: { channelId: string; startTime?: number; endTime?: number }) {
    let params = new HttpParams();
    if (startTime) {
      params = params.set('startTime', startTime.toString());
    }

    if (endTime) {
      params = params.set('endTime', endTime.toString());
    }

    return this.http
      .get<CommentModel[]>(`${this.baseUrl}/channel/${channelId}`, { params })
      .pipe(map((comments) => (comments ?? []).map((comment) => this.normalizeCommentVisibility(comment))));
  }
  /**
   *
   * @param opt
   * @returns
   */
  getCommentsOfManyChannels(opt: { channelIdentifiers: string[]; startTime?: number; endTime?: number }) {
    return this.http.post<{ comments: CommentModel[] }>(`${this.baseUrl}/channels`, opt).pipe(
      map((response) => ({
        comments: response.comments.map((comment) => this.normalizeCommentVisibility(comment)),
      }))
    );
  }

  setLocalVisibility(commentId: string, showInLane: boolean): void {
    this.localVisibilityById.set(commentId, showInLane);
  }

  private normalizeCommentVisibility(comment: CommentModel): CommentModel {
    const showInLane = this.localVisibilityById.get(comment.identifier) ?? true;
    this.localVisibilityById.set(comment.identifier, showInLane);
    return withLocalCommentVisibility(comment, showInLane);
  }
  /**
   *
   * @param id
   * @returns
   */
  deleteComment(id: string) {
    return this.http.delete(`${this.baseUrl}/${id}`).pipe(
      catchError((err) => {
        this.snack.open(err.error.reason, translate('Dismiss'));
        return [];
      })
    );
  }
}
